// src/features/dashboard/components/SystemInfoView.tsx

import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
} from '@mui/material';
import {
  Computer,
  Storage,
  Refresh,
  Schedule,
  PlayArrow,
  CheckCircle,
  Error,
  Warning,
  ViewInAr,
  DataObject,
} from '@mui/icons-material';
import { useTranslate, useNotify, useDataProvider } from 'react-admin';

interface SystemInfo {
  application: {
    version: string;
    environment: string;
    uptime: string;
    processors: number;
    startTime?: string;
    serverTime?: string;
    machineName?: string;
  };
  database: {
    status: string;
    size: string;
    provider: string;
    pendingMigrations: number;
    connected?: boolean;
  };
  hmis: {
    total: number;
    online: number;
    offline: number;
  };
  jobs: Job[];
  statistics?: {
    totalHotels?: number;
    totalZones?: number;
    totalLockers?: number;
    availableLockers?: number;
    occupiedLockers?: number;
    reservedLockers?: number;
    totalHMIs?: number;
    onlineHMIs?: number;
    activeAssignments?: number;
    pendingAssignments?: number;
    todayAssignments?: number;
    expiredActiveAssignments?: number;
  };
}

interface Job {
  id: string;
  name: string;
  queue: string;
  schedule: string;
  cron?: string;
  lastExecution?: string;
  nextExecution?: string;
  status?: string;
  error?: string;
  lastJobId?: string;
  lastJobState?: string;
  createdAt?: string;
}

const SystemInfoView: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const fetchSystemInfo = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      const { data } = await dataProvider.getSystemInfo();
      setSystemInfo(data);
    } catch (err) {
      console.error('Error loading system information:', err);
      setError('Failed to load system information');
      notify(translate('common.notifications.load_error'), { type: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const { data } = await dataProvider.getSystemJobs();
      setSystemInfo(prev => prev ? { ...prev, jobs: data } : null);
    } catch (err) {
      console.error('Error loading jobs:', err);
      notify(translate('common.notifications.load_error'), { type: 'error' });
    }
  };

  useEffect(() => {
    fetchSystemInfo();
    fetchJobs();
    
    // Set up auto-refresh every 30 minutes
    const interval = setInterval(() => {
      fetchSystemInfo();
      fetchJobs();
    }, 1800000);
    
    return () => clearInterval(interval);
  }, []);

  const handleTriggerJob = async (jobId: string, jobName: string) => {
    try {
      await dataProvider.triggerSystemJob(jobId);
      
      notify(`Job "${jobName}" triggered successfully`, { type: 'success' });
      
      // Refresh data after triggering
      setTimeout(() => {
        fetchSystemInfo();
        fetchJobs();
      }, 1000);
    } catch (err) {
      console.error('Error triggering job:', err);
      notify(`Error triggering job`, { type: 'error' });
    }
  };

  const handleRefresh = () => {
    fetchSystemInfo();
    fetchJobs();
  };

  if (loading && !systemInfo) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
          borderRadius: 2
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} thickness={3} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading system information...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error && !systemInfo) {
    return (
      <Alert 
        severity="error" 
        sx={{ 
          borderRadius: 2,
          '& .MuiAlert-icon': {
            fontSize: '28px'
          }
        }}
        action={
          <IconButton size="small" onClick={handleRefresh}>
            <Refresh />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!systemInfo) {
    return null;
  }

  const InfoRow = ({ label, value, isStatus = false }: { label: string; value: any; isStatus?: boolean }) => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      py: 1.5,
      '&:not(:last-child)': {
        borderBottom: '1px solid #f0f0f0'
      }
    }}>
      <Typography sx={{ 
        fontSize: '0.875rem',
        color: '#666',
        fontWeight: 400
      }}>
        {label}
      </Typography>
      {isStatus ? value : (
        <Typography sx={{ 
          fontSize: '0.875rem',
          color: '#1a1a1a',
          fontWeight: 500
        }}>
          {value}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      {/* System Details Card */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e5e7eb'
      }}>
        <Box sx={{ 
          p: 2.5,
          pb: 2,
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#fafbfc'
        }}>
          <Typography sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: '1rem',
            fontWeight: 600,
            color: '#1a1a1a'
          }}>
            <ViewInAr sx={{ fontSize: 20, color: '#0ea5e9' }} />
            System Details
          </Typography>
        </Box>
        
        <CardContent sx={{ p: 0 }}>
          <Grid container>
            {/* Application Section */}
            <Grid item xs={12} md={6} sx={{ 
              borderRight: { md: '1px solid #e5e7eb' }
            }}>
              <Box sx={{ p: 3 }}>
                <Typography sx={{ 
                  color: '#666',
                  mb: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em'
                }}>
                  <Computer sx={{ fontSize: 16, color: '#999' }} />
                  Application
                </Typography>
                
                <Box>
                  <InfoRow label="Version" value={systemInfo.application.version} />
                  <InfoRow 
                    label="Environment" 
                    value={
                      <Chip 
                        label={systemInfo.application.environment} 
                        size="small"
                        sx={{ 
                          height: 24,
                          fontSize: '0.75rem',
                          backgroundColor: systemInfo.application.environment === 'Production' 
                            ? 'rgba(34, 197, 94, 0.1)' 
                            : 'rgba(251, 191, 36, 0.1)',
                          color: systemInfo.application.environment === 'Production' 
                            ? '#16a34a' 
                            : '#ea580c',
                          fontWeight: 600,
                          border: 'none'
                        }}
                      />
                    }
                    isStatus
                  />
                  <InfoRow label="Uptime" value={systemInfo.application.uptime} />
                  <InfoRow label="Processors" value={systemInfo.application.processors} />
                  {systemInfo.application.machineName && (
                    <InfoRow label="Machine" value={systemInfo.application.machineName} />
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Database Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3 }}>
                <Typography sx={{ 
                  color: '#666',
                  mb: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em'
                }}>
                  <DataObject sx={{ fontSize: 16, color: '#999' }} />
                  Database
                </Typography>
                
                <Box>
                  <InfoRow 
                    label="Status" 
                    value={
                      <Typography sx={{ 
                        fontSize: '0.875rem',
                        color: systemInfo.database.status === 'Connected' ? '#16a34a' : '#dc2626',
                        fontWeight: 500
                      }}>
                        {systemInfo.database.status}
                      </Typography>
                    }
                    isStatus
                  />
                  <InfoRow label="Size" value={systemInfo.database.size} />
                  <InfoRow label="Provider" value={systemInfo.database.provider} />
                  <InfoRow 
                    label="Pending Migrations" 
                    value={
                      <Typography sx={{ 
                        fontSize: '0.875rem',
                        color: systemInfo.database.pendingMigrations > 0 ? '#dc2626' : '#1a1a1a',
                        fontWeight: 500
                      }}>
                        {systemInfo.database.pendingMigrations}
                      </Typography>
                    }
                    isStatus
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Background Jobs Card */}
      <Card sx={{ 
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2.5,
          pb: 2,
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#fafbfc'
        }}>
          <Typography sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: '1rem',
            fontWeight: 600,
            color: '#1a1a1a'
          }}>
            <Schedule sx={{ fontSize: 20, color: '#0ea5e9' }} />
            Background Jobs
          </Typography>
          <Tooltip title="Refresh Jobs">
            <IconButton 
              size="small" 
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ 
                color: '#666',
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                  color: '#0ea5e9'
                }
              }}
            >
              <Refresh sx={{ 
                fontSize: 18,
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }} />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafbfc' }}>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.75rem', 
                  color: '#666', 
                  borderBottom: '1px solid #e5e7eb',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em'
                }}>
                  Job Name
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.75rem', 
                  color: '#666', 
                  borderBottom: '1px solid #e5e7eb',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em'
                }}>
                  Queue
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.75rem', 
                  color: '#666', 
                  borderBottom: '1px solid #e5e7eb',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em'
                }}>
                  Schedule
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.75rem', 
                  color: '#666', 
                  borderBottom: '1px solid #e5e7eb',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em'
                }}>
                  Last Execution
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.75rem', 
                  color: '#666', 
                  borderBottom: '1px solid #e5e7eb',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em'
                }}>
                  Next Execution
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.75rem', 
                  color: '#666', 
                  borderBottom: '1px solid #e5e7eb',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em'
                }}>
                  Status
                </TableCell>
                <TableCell align="center" sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.75rem', 
                  color: '#666', 
                  borderBottom: '1px solid #e5e7eb',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em'
                }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {systemInfo.jobs && systemInfo.jobs.length > 0 ? (
                systemInfo.jobs.map((job) => (
                  <TableRow 
                    key={job.id} 
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: '#fafbfc'
                      },
                      '& td': {
                        borderBottom: '1px solid #f0f0f0'
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 500, 
                          fontSize: '0.875rem', 
                          color: '#1a1a1a' 
                        }}>
                          {job.name}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: '#999', 
                          fontSize: '0.7rem' 
                        }}>
                          {job.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.queue}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          backgroundColor: job.queue === 'critical' 
                            ? 'rgba(239, 68, 68, 0.1)' 
                            : '#f3f4f6',
                          color: job.queue === 'critical' 
                            ? '#dc2626' 
                            : '#666',
                          border: 'none'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ 
                          fontSize: '0.875rem', 
                          color: '#1a1a1a' 
                        }}>
                          {job.schedule}
                        </Typography>
                        {job.cron && (
                          <Typography variant="caption" sx={{ 
                            color: '#999', 
                            fontSize: '0.7rem', 
                            fontFamily: 'monospace' 
                          }}>
                            {job.cron}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        fontSize: '0.875rem', 
                        color: '#666' 
                      }}>
                        {job.lastExecution || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        fontSize: '0.875rem', 
                        color: '#666' 
                      }}>
                        {job.nextExecution || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {job.status && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {job.status === 'Success' || job.status === 'Succeeded' ? (
                            <CheckCircle sx={{ fontSize: 14, color: '#16a34a' }} />
                          ) : job.status === 'Failed' ? (
                            <Error sx={{ fontSize: 14, color: '#dc2626' }} />
                          ) : job.status === 'Running' ? (
                            <Schedule sx={{ fontSize: 14, color: '#f59e0b' }} />
                          ) : (
                            <Warning sx={{ fontSize: 14, color: '#999' }} />
                          )}
                          <Typography variant="caption" sx={{ 
                            color: job.status === 'Success' || job.status === 'Succeeded' ? '#16a34a' : 
                                   job.status === 'Failed' ? '#dc2626' : 
                                   job.status === 'Running' ? '#f59e0b' : '#999',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            {job.status}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Trigger Job Now">
                        <IconButton 
                          size="small"
                          onClick={() => handleTriggerJob(job.id, job.name)}
                          sx={{
                            color: '#999',
                            '&:hover': {
                              backgroundColor: '#f3f4f6',
                              color: '#0ea5e9'
                            }
                          }}
                        >
                          <PlayArrow sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No background jobs found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </>
  );
};

export default SystemInfoView;